import React, { useState, useEffect } from 'react';
import { profileService } from '../../../services/profileService';
import { useAuth } from '../../../context/AuthContext';

const Profile = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const response = await profileService.getProfile();
        if (response.success && response.user) {
          const userData = response.user;
          setFormData({
            fullName: userData.fullName || '',
            email: userData.email || '',
            phone: userData.phone || '',
          });
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
        setMessage({
          type: 'error',
          text: error.message || 'Failed to load profile data'
        });
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const dataToSubmit = {
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim()
      };

      const response = await profileService.updateProfile(dataToSubmit);
      
      if (response.success) {
        setMessage({ 
          type: 'success', 
          text: 'Profile updated successfully!' 
        });
        
        if (user && response.user) {
          localStorage.setItem('kapta_user', JSON.stringify(response.user));
        }
      } else {
        setMessage({ 
          type: 'error', 
          text: response.message || 'Failed to update profile' 
        });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message || 'An error occurred while updating profile' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-2 sm:px-0">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Profile Information</h1>
      <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
        Keep your details up to date so we can reach you when memories are being prepared or delivered.
      </p>

      {message.text && (
        <div
          className={`mb-4 sm:mb-6 p-3 sm:p-4 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="w-full">
        <div className="space-y-4 sm:space-y-6">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-normal text-secondary mb-2">
              Full name
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className="w-full p-3 sm:p-4 bg-[#F5F5F5] border border-[#E4E4E4] focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-colors text-sm sm:text-base"
              placeholder="Amaka Johnson"
              disabled={loading}
              required
            />
          </div>

          {/* Email Address */}
          <div>
            <label className="block text-sm font-normal text-secondary mb-2">
              Email address
            </label>
            <div className="relative flex flex-col sm:flex-row sm:items-center gap-2">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="flex-1 p-3 sm:p-4 bg-[#F5F5F5] border border-[#E4E4E4] focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-colors text-sm sm:text-base"
                placeholder="amibaby411@gmail.com"
                disabled={loading}
                required
              />
              <button
                type="button"
                className="text-[#D4A574] hover:text-[#C49564] font-medium text-sm transition-colors disabled:opacity-50 whitespace-nowrap"
                onClick={() => console.log('Change email clicked')}
                disabled={loading}
              >
                Change email
              </button>
            </div>
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-normal text-secondary mb-2">
              Phone number
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="tel"
                value="+234"
                className="w-full sm:w-24 p-3 sm:p-4 bg-[#F5F5F5] border border-[#E4E4E4] focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-colors text-sm sm:text-base"
                disabled
              />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="flex-1 p-3 sm:p-4 bg-[#F5F5F5] border border-[#E4E4E4] focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-colors text-sm sm:text-base"
                placeholder="806 123 4567"
                disabled={loading}
              />
            </div>
          </div>

          {/* Save Changes Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 w-full sm:w-auto min-w-[200px] bg-[#D2A146] text-white font-medium hover:bg-[#C49564] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Profile;